import { useState } from "react";
import { Link } from "react-router";
import type { CustomerContact } from "../types";
import { useCustomerContacts } from "../api";
import { Card, CardBody, Select, SelectItem } from "@heroui/react";
import { MapPin, User, Phone, Mail, Star, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslation } from "react-i18next";

interface AddressBookSelectorProps {
  onSelect: (contact: CustomerContact) => void;
  selectedContactId?: string;
  label?: string;
  className?: string;
  showDeliveryInstructions?: boolean;
}

export function AddressBookSelector({
  onSelect,
  selectedContactId,
  label,
  className,
  showDeliveryInstructions = true,
}: AddressBookSelectorProps) {
  const { t } = useTranslation("app");
  const resolvedLabel = label ?? t("addressBook.selector.defaultLabel");
  const [selectedId, setSelectedId] = useState(selectedContactId);

  const { data, isLoading } = useCustomerContacts({});

  if (isLoading) {
    return (
      <div className={cn("text-sm text-default-500", className)}>
        {t("addressBook.selector.loading")}
      </div>
    );
  }

  const contacts = data?.contacts ?? [];
  const selectedContact = contacts.find((c) => c.id === selectedId);

  if (!contacts.length) {
    return (
      <div className={cn("text-sm text-default-500", className)}>
        {t("addressBook.selector.emptyPrefix")}{" "}
        <Link
          to="/my-address-book"
          className="inline-flex items-center gap-1 text-primary hover:underline"
        >
          <Plus className="h-3 w-3" />
          {t("addressBook.selector.addContacts")}
        </Link>{" "}
        {t("addressBook.selector.emptySuffix")}
      </div>
    );
  }

  const handleSelect = (contactId: string) => {
    setSelectedId(contactId);
    const contact = contacts.find((c) => c.id === contactId);
    if (contact) {
      onSelect(contact);
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      <Select
        aria-label={resolvedLabel}
        placeholder={resolvedLabel}
        variant="flat"
        selectedKeys={selectedId ? [selectedId] : []}
        onSelectionChange={(keys) => {
          const next = Array.from(keys)[0] as string | undefined;
          if (next) handleSelect(next);
        }}
      >
        {contacts.map((contact) => (
          <SelectItem
            key={contact.id}
            startContent={
              contact.isDefault ? (
                <Star className="h-3 w-3 fill-warning text-warning" />
              ) : null
            }
            textValue={contact.contactName}
          >
            <div className="flex items-center gap-2">
              <span>{contact.contactName}</span>
              {contact.label && (
                <span className="text-xs text-default-500">
                  ({contact.label})
                </span>
              )}
            </div>
          </SelectItem>
        ))}
      </Select>

      {selectedContact && (
        <Card shadow="sm">
          <CardBody className="pt-4 space-y-2">
            <div className="flex items-start gap-2 text-sm">
              <User className="h-4 w-4 mt-0.5 text-default-500" />
              <div>
                <div className="font-medium">{selectedContact.contactName}</div>
                {selectedContact.contactCompany && (
                  <div className="text-default-500">
                    {selectedContact.contactCompany}
                  </div>
                )}
              </div>
            </div>

            {(selectedContact.contactPhone ?? selectedContact.contactEmail) && (
              <div className="space-y-1">
                {selectedContact.contactPhone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-default-500" />
                    <span>{selectedContact.contactPhone}</span>
                  </div>
                )}
                {selectedContact.contactEmail && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-default-500" />
                    <span>{selectedContact.contactEmail}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-start gap-2 text-sm pt-2 border-t border-default-200">
              <MapPin className="h-4 w-4 mt-0.5 text-default-500" />
              <div>
                <div>{selectedContact.addressLine1}</div>
                {selectedContact.addressLine2 && (
                  <div>{selectedContact.addressLine2}</div>
                )}
                <div>
                  {selectedContact.addressCity},{" "}
                  {selectedContact.addressProvince}{" "}
                  {selectedContact.addressPostalCode}
                </div>
                <div>{selectedContact.addressCountry}</div>
              </div>
            </div>

            {(selectedContact.pickupInstructions ||
              (showDeliveryInstructions &&
                selectedContact.deliveryInstructions)) && (
              <div className="text-xs text-default-500 pt-2 border-t border-default-200 space-y-1">
                {selectedContact.pickupInstructions && (
                  <div>
                    <strong>{t("addressBook.selector.pickup")}</strong>{" "}
                    {selectedContact.pickupInstructions}
                  </div>
                )}
                {showDeliveryInstructions &&
                  selectedContact.deliveryInstructions && (
                    <div>
                      <strong>{t("addressBook.selector.delivery")}</strong>{" "}
                      {selectedContact.deliveryInstructions}
                    </div>
                  )}
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
}
